import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { getI18n, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { keyframes } from "@mui/system";
import { cloneElement, useEffect, useState } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LoginUser from "src/authentication/features/login/loginUser";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import { useAuth } from "src/shared/context/useAuth";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import LandPageAppsDialog from "src/shared/Layouts/LandPageAppsDialog";
import RegisterModal from "src/welcome/components/RegisterModal";
import PersonIcon from "@mui/icons-material/Person";
import ChipMessage from "src/shared/components/ChipMessage";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";

const fadeIn = keyframes`
	from {
		opacity: 0
	}
	to {
		opacity: 1
	}
`;

export type TAppSublists = {
  primary: boolean;
  a: boolean;
  b: boolean;
  c: boolean;
  d: boolean;
  e: boolean;
  f: boolean;
};

function LandPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const [openLoginModal, setOpenLoginModal] = useState<boolean>(
    location.pathname.split("/")[1] === "" ||
      location.pathname === "/welcome/ggpsIntro"
      ? false
      : true,
  );
  const [selectedUser, setSelectedUser] = useState<number | null>(
    location.pathname.split("/")[1] == "welcome"
      ? 0
      : location.pathname.split("/")[1] == "welcomeNonGemi"
        ? 1
        : location.pathname.split("/")[1] == "welcomeGovChamber"
          ? 2
          : location.pathname.split("/")[1] == "welcomeGovRegion"
            ? 3
            : null,
  );

  const { authenticated, userHomePath } = useAuth();

  const [openCertificateDialog, setOpenCertificateDialog] =
    useState<boolean>(false);
  const [openCertificates, setOpenCertificates] = useState<boolean>(false);
  const [openCertificatesForN01, setOpenCertificatesForN01] =
    useState<boolean>(false);

  const [openApplicationDialog, setOpenApplicationDialog] =
    useState<boolean>(false);
  const [applicationSublistsOpen, setApplicationSublistsOpen] =
    useState<TAppSublists>({
      primary: false,
      a: false,
      b: false,
      c: false,
      d: false,
      e: false,
      f: false,
    });

  const [openRegisterModal, setOpenRegisterModal] = useState<boolean>(
    location.pathname === "/welcome/ggpsIntro",
  );

  const cards = [
    {
      image: (
        <DescriptionOutlinedIcon
          sx={{ width: "4rem", height: "4rem", color: "#003476" }}
        />
      ),
      title: t("Πιστοποιητικά και Αντίγραφα"),
      content: t("Υπηρεσία Ηλεκτρονικής Χορήγησης Πιστοποιητικών & Αντιγράφων"),
      onClick: () => {
        setOpenCertificateDialog(true);
      },
    },
    {
      image: (
        <ArticleOutlinedIcon
          sx={{ width: "4rem", height: "4rem", color: "#003476" }}
        />
      ),
      title: t("Αιτήσεις"),
      content: t(
        "Υπηρεσία Ηλεκτρονικής Υποβολής Αιτήσεων Καταχώρισης στο ΓΕΜΗ",
      ),
      onClick: () => {
        setOpenApplicationDialog(true);
      },
    },
    {
      image: (
        <TaskOutlinedIcon
          sx={{ width: "4rem", height: "4rem", color: "#003476" }}
        />
      ),
      title: t("Έλεγχος Γνησιότητας"),
      content: t("Έλεγχος Γνησιότητας για Πιστοποιητικά & Αντίγραφα"),
      onClick: () => {
        navigate("/publicCertificateDownload/form");
      },
    },
    {
      image: (
        <SearchOutlinedIcon
          sx={{ width: "4rem", height: "4rem", color: "#003476" }}
        />
      ),
      title: t("Δημοσιότητα"),
      content: t("Αναζητήστε πληροφορίες για Επιχειρήσεις"),
      onClick: () => {
        window.open(
          import.meta.env.VITE_PUBLICITY_URL,
          "_blank",
          "noopener,noreferrer",
        );
      },
    },
  ];

  const loginCards: {
    title: string;
    image: JSX.Element;
    description: string;
    endpoint: string;
    type: "user" | "nonGemi" | "chamber" | "region";
  }[] = [
    {
      title: t("Επιχείρηση"),
      image: <BusinessOutlinedIcon sx={{ color: "#003476" }} />,
      description: `${t("Για Επιχειρήσεις & Νομικά Πρόσωπα")} (${t("Υπόχρεοι Γ.Ε.ΜΗ.")})`,
      endpoint: "/welcome/loginForm",
      type: "user",
    },
    {
      title: t("Ενδιαφερόμενος"),
      image: <PersonIcon sx={{ color: "#003476" }} />,
      description: t("Για Φυσικά Πρόσωπα & Ενδιαφερόμενους"),
      endpoint: "/welcomeNonGemi/loginForm",
      type: "nonGemi",
    },
    {
      title: t("Υ. Γ.Ε.ΜΗ."),
      image: <AccountCircleOutlinedIcon sx={{ color: "#003476" }} />,
      description: t("Για Υπαλλήλους της Υπηρεσίας Γ.Ε.ΜΗ."),
      endpoint: "/welcomeGovChamber/login",
      type: "chamber",
    },
  ];

  useEffect(() => {
    if (authenticated) {
      const attemptedRoute = sessionStorage.getItem("attemptedRoute");

      if (attemptedRoute) {
        navigate(attemptedRoute);
        sessionStorage.removeItem("attemptedRoute");
      } else {
        navigate(userHomePath);
      }
    }
  }, [authenticated, openLoginModal]);

  const handleCloseModal = () => {
    setOpenLoginModal(false);
    setSelectedUser(null);
    window.history.replaceState(null, "", "/");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "start",
            width: "100%",
            minHeight: "70vh",
            letterSpacing: ".1em",
            gap: 4,
            textWrap: "wrap",
            flexWrap: "wrap",
            textAlign: "center",
            p: 2,
            background: "#f2f8ff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: {
                md: "space-between",
                xs: "center",
              },
              alignItems: "center",
              width: { md: "70%", xs: "100%" },
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <IconButton
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              onClick={() => {
                window.open(
                  "https://www.mindev.gov.gr/",
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <Box
                component="img"
                src={
                  getI18n().language == "el"
                    ? "/images/YpourgeioNeo_2023-08-03.png"
                    : "/images/YpourgeioNeo-eng-2023-08-03.png"
                }
              />
            </IconButton>
            <IconButton
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              onClick={() => {
                window.open("https://uhc.gr/", "_blank", "noopener,noreferrer");
              }}
            >
              <Box
                component="img"
                sx={{ width: 200, height: 100 }}
                src={"/images/logoIndex1.png"}
              />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              variant="h3"
              sx={{ color: "#003476", textAlign: "center" }}
            >
              {t("Γενικό Εμπορικό Μητρώο")}
            </Typography>

            <Typography variant="h6" sx={{ color: "#6b7280" }}>
              {t("Προσωποποιημένες Υπηρεσίες Γ.Ε.ΜΗ. προς τις Επιχειρήσεις")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "60%",
              mt: 2,
            }}
          >
            <Typography
              component="p"
              fontWeight="light"
              sx={{
                animation: `${fadeIn} .5s ease-out`,
                fontSize: "1.05rem",
                textAlign: "center",
              }}
            >
              {t(
                "Η Κεντρική Υπηρεσία ΓΕΜΗ της ΚΕΕ σας καλωσορίζει στη νέα «Υπηρεσία Ηλεκτρονικής Υποβολής» μια πρωτοποριακή Υπηρεσία που σκοπό έχει να απαλλάξει τόσο τις εμπορικές επιχειρήσεις όσο και τις Υπηρεσίες ΓΕΜΗ και τις Υπηρεσίες των Περιφερειών από την άσκοπη και χρονοβόρα γραφειοκρατική διαδικασία, που έως σήμερα υποβάλλονται, για την υποβολή αιτήσεων χορήγησης πιστοποιητικών και αιτήσεων καταχώρισης στο ΓΕΜΗ.",
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mt: 5,
              flexWrap: "wrap",
            }}
          >
            <Button
              sx={{
                border: "2px solid #e7e7ea",
                background: "white",
                color: "black",
                px: 15,
                "&:hover": {
                  backgroundColor: "#f4f4f5",
                },
              }}
              onClick={() => setOpenRegisterModal(true)}
              size="large"
            >
              {t("Εγγραφή")}
            </Button>
            <Button
              variant="contained"
              sx={{
                px: 15,
                background: "#003476",
                "&:hover": {
                  backgroundColor: "#00449c",
                },
              }}
              onClick={() => setOpenLoginModal(true)}
              size="large"
            >
              {t("Είσοδος")}
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#f3f4f6",
            width: "100%",
            display: "flex",
            minHeight: "30vh",
            flexWrap: "wrap",
            gap: 15,
            justifyContent: "center",
          }}
        >
          {cards.map((card) => (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "20rem",
                height: "20rem",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {card.image}
              <Typography variant="h6">{card.title}</Typography>
              <Typography
                variant="body1"
                sx={{ color: "#6b7280", textAlign: "center" }}
              >
                {card.content}
              </Typography>
              <Button onClick={card.onClick}>{t("Μάθετε Περισσότερα")}</Button>
            </Box>
          ))}
        </Box>
      </Box>
      <Dialog
        open={openLoginModal}
        onClose={handleCloseModal}
        fullWidth
        sx={{ borderRadius: "1rem" }}
      >
        <DialogTitle
          variant="h6"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={"/images/favicon.ico"}
                alt={"Γ.Ε.ΜΗ. favicon"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.15,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#003476" }}
              >
                {t("Γ.Ε.ΜΗ.")}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "#6b7280" }}>
                {t("Γενικό Εμπορικό Μητρώο")}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseModal}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
            mt: "1rem",
            pb: 5,
          }}
        >
          {selectedUser == null &&
            loginCards.map((user, index) => (
              <Box
                key={index}
                sx={{
                  border: "1px solid #f0f0f2",
                  minHeight: "5rem",
                  width: "100%",
                  borderRadius: ".5rem",
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  px: 2,
                  gap: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f4f4f5",
                  },
                }}
                onClick={() => {
                  setSelectedUser(index);
                  window.history.replaceState(
                    null,
                    "",
                    loginCards[index].endpoint,
                  );
                }}
              >
                {user.image}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.1rem",
                  }}
                >
                  <Typography>
                    {t("Είσοδος ως")} {user.title}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: "#9599a4" }}>
                    {user.description}
                  </Typography>
                </Box>
              </Box>
            ))}

          {selectedUser != null && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    width: "fit-content",
                    maxWidth: "100%",
                    alignSelf: "flex-start",
                    justifyContent: "flex-end",
                  }}
                >
                  <ChipMessage
                    message={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "0.5rem",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          color: "#002d6e",
                        }}
                      >
                        {cloneElement(loginCards[selectedUser].image, {
                          sx: {
                            ...(loginCards[selectedUser].image.props.sx || {}),
                            color: "#002d6e",
                            fontSize: "1.75rem",
                            display: "flex",
                            alignItems: "center",
                          },
                        })}
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            lineHeight: 1,
                          }}
                        >
                          {`${t("Είσοδος ως")} ${loginCards[selectedUser].title}`}
                        </Typography>
                      </Box>
                    }
                    variant={MessageVariant.Info}
                  />
                </Box>
                <Button
                  variant="text"
                  sx={{
                    alignSelf: "start",
                    mb: "2rem",
                    textDecoration: "underline",
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                  onClick={() => {
                    setSelectedUser(null);
                    window.history.replaceState(null, "", "/");
                  }}
                >
                  <KeyboardBackspaceOutlinedIcon sx={{ width: 20 }} />
                  {t("Επιστροφή στις Επιλογές")}
                </Button>
              </Box>
              <LoginUser type={loginCards[selectedUser].type} />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog maxWidth="xl" open={openCertificateDialog}>
        <DialogTitle
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("Υπηρεσία Ηλεκτρονικής Χορήγησης Πιστοποιητικών & Αντιγράφων")}
          </Typography>
          <IconButton onClick={() => setOpenCertificateDialog(false)}>
            <CloseOutlinedIcon sx={{ width: 30, height: 30 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Με τη νέα αυτή Υπηρεσία, τόσο η υποβολή αιτήσεων για τη χορήγηση πιστοποιητικών, βεβαιώσεων, αποσπασμάτων και αντιγράφων πράξεων ή στοιχείων που είναι καταχωρισμένα στο ΓΕΜΗ, όσο και η έκδοσή τους, πραγματοποιούνται αποκλειστικά ηλεκτρονικά.",
              )}
            </Typography>
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Η Υπηρεσία υλοποιήθηκε κατ’ εφαρμογή του άρθρου 8 του ν. 4919/2022, όπως ισχύει, καθώς και της Κοινής Υπουργικής Απόφασης Κ2-4946/2014.",
              )}
            </Typography>
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Σύμφωνα με το ισχύον πλαίσιο, κάθε ενδιαφερόμενος που επιθυμεί να λάβει πιστοποιητικό ή αντίγραφο πράξης ή στοιχείου καταχωρισμένου στο ΓΕΜΗ, διεκπεραιώνει το σύνολο της διαδικασίας —συμπεριλαμβανομένης της υποβολής της αίτησης και της καταβολής των σχετικών τελών— μέσω ειδικού ηλεκτρονικού περιβάλλοντος. Η πρόσβαση πραγματοποιείται με προσωπικούς κωδικούς χρήστη, οι οποίοι εκδίδονται και χορηγούνται από την Κεντρική Υπηρεσία Γ.Ε.ΜΗ.",
              )}
            </Typography>
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Η Υπηρεσία είναι διαθέσιμη σε κάθε ενδιαφερόμενο, ανεξαρτήτως του αν είναι εγγεγραμμένος στο ΓΕΜΗ.",
              )}
            </Typography>
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Η διαδικασία έκδοσης των αιτούμενων πιστοποιητικών, βεβαιώσεων, αντιγράφων και αποσπασμάτων ολοκληρώνεται από την κατά περίπτωση αρμόδια Υπηρεσία ΓΕΜΗ, ενώ τα έγγραφα εκδίδονται ηλεκτρονικά και φέρουν ψηφιακή υπογραφή.",
              )}
            </Typography>
            <Typography component="p" sx={{ lineHeight: "2rem" }}>
              {t(
                "Για όλα τα νομικά πρόσωπα που είναι υπόχρεα εγγραφής στο ΓΕΜΗ θα είναι διαθέσιμα ηλεκτρονικά τα παρακάτω πιστοποιητικά:",
              )}
            </Typography>
          </Box>
          <List>
            <ListItemButton
              onClick={() => setOpenCertificates(!openCertificates)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <ListItemText
                primaryTypographyProps={{
                  fontWeight: "bold",
                  variant: "h6",
                }}
                primary={t("Πιστοποιητικά για νομικό πρόσωπο")}
              />
              <MenuOutlinedIcon />
            </ListItemButton>
            <Collapse in={openCertificates} timeout="auto" unmountOnExit>
              <List component="div" sx={{ padding: 1 }}>
                <ListItem>
                  <ListItemText primary={t("Γενικό Πιστοποιητικό")} />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t("Αμιγές Πιστοποιητικό Ιστορικότητας")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t("Πιστοποιητικό Εκπροσώπησης")} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t("Άλλο (ελεύθερης μορφής)")} />
                </ListItem>
              </List>
            </Collapse>
          </List>
        </DialogContent>
      </Dialog>

      <LandPageAppsDialog
        openApplicationDialog={openApplicationDialog}
        setOpenApplicationDialog={setOpenApplicationDialog}
        applicationSublistsOpen={applicationSublistsOpen}
        setApplicationSublistsOpen={setApplicationSublistsOpen}
      />
      <RegisterModal
        open={openRegisterModal}
        setOpen={setOpenRegisterModal}
        setOpenLoginModal={setOpenLoginModal}
        setSelectedUser={setSelectedUser}
      />
    </>
  );
}

export default LandPage;
